var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var LookupConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
import { lookupConfigSchemaValidation } from '../validations.generated';
export const lookupConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:LookupConfig',
    type: 'object',
    properties: {
        leftColumn: { type: 'string' },
        rightColumn: { type: 'string' },
        addToTopColumns: {
            type: 'array',
            items: { type: 'string' }
        }
    },
    required: ['leftColumn', 'rightColumn', 'addToTopColumns']
};
let LookupConfig = LookupConfig_1 = class LookupConfig extends ToolConfig {
    constructor(leftColumn, rightColumn, addToTopColumns, customFormats) {
        super(ToolType.LOOKUP, customFormats);
        this.leftColumn = leftColumn;
        this.rightColumn = rightColumn;
        this.addToTopColumns = addToTopColumns;
        JSON.validate(this, LookupConfig_1);
    }
    get left() {
        return LookupConfig_1.left;
    }
    get right() {
        return LookupConfig_1.right;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return LookupConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return LookupConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return LookupConfig_1.outs;
    }
    static get left() {
        return LookupConfig_1.ins[0];
    }
    static get right() {
        return LookupConfig_1.ins[1];
    }
    static get out() {
        return LookupConfig_1.outs[0];
    }
    static get ins() {
        return ['left', 'right'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.LOOKUP.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.COMBINE], this.ins, this.outs, ['merge', 'vlookup'], false);
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const exp = tsb.export(this.out);
        const left = ins.schemas.at(this.left);
        const right = ins.schemas.at(this.right);
        const leftCol = this.leftColumn;
        const rightCol = this.rightColumn;
        // generate the dependents, dependents would be the join columns on the left/right sides
        const dependents = [];
        left.forEach((colMet) => {
            if (leftCol === colMet.name)
                dependents.push(colMet);
        });
        right
            .filter((colMet) => colMet.name in this.addToTopColumns)
            .forEach((colMet) => {
            if (rightCol === colMet.name)
                dependents.push(colMet);
        });
        const topColumns = left.map((l) => l.name);
        const bottomColumns = right.map((r) => r.name);
        const aliases = ToolConfig.computeAliases(topColumns, bottomColumns);
        left.forEach((c) => {
            if (leftCol === c.name)
                exp.copy(this.leftColumn, c, false, true, dependents);
            else
                exp.copy(this.leftColumn, c, false, false);
        });
        this.addToTopColumns.forEach((addCols) => {
            const filteredCol = right.filter((colMet) => colMet.name === addCols);
            if (filteredCol.length > 0) {
                const col = filteredCol[0];
                if (rightCol === col?.name)
                    exp.copy(this.rightColumn, col, false, true, dependents, aliases.get(col.name));
                else
                    exp.copy(this.rightColumn, col, false, false, undefined, aliases.get(col.name));
            }
        });
        exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const left = scope.schemas.at(this.left);
        const right = scope.schemas.at(this.right);
        if (!left || !right) {
            return ToolValidationResult.getPortMissingEdgesResult('One or more input nodes are missing.', 'The look up tool requires two input nodes.');
        }
        const inputs = new Map();
        const findLeft = left.find((col) => col.name === this.leftColumn);
        if (!findLeft) {
            return ToolValidationResult.getBadMetadataResult(`The left column '${this.leftColumn}' is missing from the left input node.`, `Please select a column from the left input node. Available columns: ${left.map((col) => col.name).join(', ')}`);
        }
        for (const addToTopColumn of this.addToTopColumns) {
            const rightFind = right.find((col) => col.name === addToTopColumn);
            if (!rightFind) {
                return ToolValidationResult.getBadMetadataResult(`The right column '${addToTopColumn}' is missing from the right input node.`, `Please select a column from the right input node. Available columns: ${right
                    .map((col) => col.name)
                    .join(', ')}`);
            }
        }
        if (findLeft.type === 'number' || findLeft.type === 'decimal') {
            inputs.set(this.rightColumn, ['number', 'decimal']);
        }
        else {
            inputs.set(this.rightColumn, [findLeft.type]);
        }
        const sourceValidationResult = validateSourceWithDetail(right, inputs, []);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
LookupConfig = LookupConfig_1 = __decorate([
    JsonSchemaV2(lookupConfigSchema, lookupConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.LOOKUP)
], LookupConfig);
export { LookupConfig };
//# sourceMappingURL=lookupConfig.js.map